import React from "react";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { UserRoleFormValues, ModuleAction, Field } from "src/models/UserRole.model";
import useUserAccess from "src/hooks/useUserAccess";

type Props = {
  formikProps: FormikProps<UserRoleFormValues>;
  onClose: () => void;
  formType: "ADD" | "EDIT";
};

const UserRoleForm = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  const { userAccess, isDataLoading } = useUserAccess();

  // Safe check for module selection
  const isModuleSelected = (moduleId: string) => {
    return Array.isArray(values?.module) 
      ? values?.module?.some((m) => m?.moduleId === moduleId)
      : false;
  };

  // Safe check for action selection
  const isActionSelected = (moduleId: string, actionId: string) => {
    const module = Array.isArray(values?.module) 
      ? values?.module?.find((m) => m?.moduleId === moduleId)
      : undefined;
    return Array.isArray(module?.moduleAction)
      ? module?.moduleAction?.some((a) => a?.actionId === actionId)
      : false;
  };

  // Safe check for field selection
  const isFieldSelected = (moduleId: string, actionId: string, fieldId: string) => {
    const module = Array.isArray(values?.module)
      ? values?.module?.find((m) => m?.moduleId === moduleId)
      : undefined;
    const action = Array.isArray(module?.moduleAction)
      ? module?.moduleAction?.find((a) => a?.actionId === actionId)
      : undefined;
    return Array.isArray(action?.fields)
      ? action?.fields?.some((f) => f?.fieldId === fieldId)
      : false;
  };

  // Toggle module selection with safe array handling
  const toggleModuleSelection = (moduleId: string, moduleName: string) => {
    const currentModules = Array.isArray(values?.module) ? [...values.module] : [];
    const exists = currentModules.some((m) => m?.moduleId === moduleId);

    if (exists) {
      const updated = currentModules.filter((m) => m?.moduleId !== moduleId);
      setFieldValue("module", updated);
    } else {
      setFieldValue("module", [
        ...currentModules,
        { moduleId, moduleName, moduleAction: [] },
      ]);
    }
  };

  // Toggle action selection with safe array handling
  const toggleActionSelection = (moduleId: string, action: ModuleAction) => {
    const currentModules = Array.isArray(values?.module) ? [...values.module] : [];
    const moduleIndex = currentModules.findIndex((m) => m?.moduleId === moduleId);

    if (moduleIndex === -1) {
      // Add new module if not exists
      setFieldValue("module", [
        ...currentModules,
        {
          moduleId,
          moduleName: userAccess?.find((m) => m.moduleId === moduleId)?.moduleName || "",
          moduleAction: [{ ...action, fields: [] }],
        },
      ]);
      return;
    }

    const updatedModules = [...currentModules];
    const currentModule = { ...updatedModules[moduleIndex] };
    const currentActions = Array.isArray(currentModule.moduleAction) 
      ? [...currentModule.moduleAction] 
      : [];

    const actionIndex = currentActions.findIndex(
      (a) => a?.actionId === action.actionId
    );

    if (actionIndex !== -1) {
      currentActions.splice(actionIndex, 1);
    } else {
      currentActions.push({ ...action, fields: [] });
    }

    currentModule.moduleAction = currentActions;

    if (currentActions.length === 0) {
      updatedModules.splice(moduleIndex, 1);
    } else {
      updatedModules[moduleIndex] = currentModule;
    }

    setFieldValue("module", updatedModules);
  };

  // Toggle field selection with safe array handling
  const toggleFieldSelection = (
    moduleId: string,
    actionId: string,
    field: Field
  ) => {
    const currentModules = Array.isArray(values?.module) ? [...values.module] : [];
    const updatedModules = currentModules.map((mod) => {
      if (mod?.moduleId !== moduleId) return mod;

      const currentActions = Array.isArray(mod.moduleAction) 
        ? [...mod.moduleAction] 
        : [];
      const updatedActions = currentActions.map((act) => {
        if (act?.actionId !== actionId) return act;

        const currentFields = Array.isArray(act.fields) ? [...act.fields] : [];
        const exists = currentFields.some((f) => f?.fieldId === field.fieldId);
        const newFields = exists
          ? currentFields.filter((f) => f?.fieldId !== field.fieldId)
          : [...currentFields, { ...field, fieldValue: "" }];

        return { ...act, fields: newFields };
      });

      return { ...mod, moduleAction: updatedActions };
    });

    setFieldValue("module", updatedModules);
  };

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add User Role" : "Edit User Role"}
        </div>
        <button 
          type="button" 
          className="hover:bg-hover text-sm" 
          onClick={onClose}
        >
          Cancel
        </button>
      </div>

      <ATMTextField
        name="roleName"
        required
        value={values?.roleName || ""}
        onChange={(e) => setFieldValue("roleName", e.target.value)}
        placeholder="Enter Role Name"
        label="Role Name"
        onBlur={handleBlur}
      />

      {!isDataLoading && Array.isArray(userAccess) && userAccess.length > 0 && (
        <div className="flex flex-col gap-4">
          {userAccess.map((module) => {
            if (!module?.moduleId) return null;
            
            const isSelected = isModuleSelected(module.moduleId);
            return (
              <div key={module.moduleId} className="border rounded-md">
                <div className="p-3 bg-gray-100">
                  <label className="flex items-center gap-2 font-semibold">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() =>
                        toggleModuleSelection(module.moduleId, module.moduleName)
                      }
                    />
                    {module.moduleName?.toUpperCase()}
                  </label>
                </div>

                {isSelected && Array.isArray(module.moduleAction) && (
                  <div className="p-3 flex gap-4 bg-white">
                    {module.moduleAction.map((action:ModuleAction) => {
                      if (!action?.actionId) return null;
                      
                      const actionSelected = isActionSelected(
                        module.moduleId,
                        action.actionId
                      );
                      return (
                        <div key={action.actionId} className="flex flex-col gap-2 ml-4">
                          <label className="flex items-center gap-2 font-medium">
                            <input
                              type="checkbox"
                              checked={actionSelected}
                              onChange={() =>
                                toggleActionSelection(module.moduleId, action)
                              }
                            />
                            {action.actionName}
                          </label>

                          {actionSelected && Array.isArray(action.fields) && action.fields.length > 0 && (
                            <div className="ml-6 flex flex-col gap-2">
                              {action.fields.map((field:Field) => {
                                if (!field?.fieldId) return null;
                                
                                const fieldSelected = isFieldSelected(
                                  module.moduleId,
                                  action.actionId,
                                  field.fieldId
                                );
                                return (
                                  <label
                                    key={field.fieldId}
                                    className="flex items-center gap-2 text-sm"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={fieldSelected}
                                      onChange={() =>
                                        toggleFieldSelection(
                                          module.moduleId,
                                          action.actionId,
                                          field
                                        )
                                      }
                                    />
                                    {field.fieldName}
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ATMLoadingButton isLoading={isSubmitting} type="submit">
        Save
      </ATMLoadingButton>
    </div>
  );
};

export default UserRoleForm;